import React, { ChangeEvent, useEffect, useState } from 'react';
import { Alert, Badge, Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ListItemIcon, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { AddRounded, ArrowForwardIosRounded, CancelRounded, CheckCircleRounded, CheckRounded, FiberNewRounded, FilterListRounded, MopedRounded, PersonSearchRounded, RunCircleRounded } from '@mui/icons-material';
import { Query, QueryDocumentSnapshot, collection, doc, endBefore, getCountFromServer, getDoc, getDocs, getFirestore, limit, limitToLast, orderBy, query, startAfter, startAt, where } from 'firebase/firestore';
import uniformRequestConverter from '../../../converters/uniformRequestConverter';
import { UniformRequest, UniformRequestStatus, UniformRequestType } from '../../../types/UniformRequest';
import { DateTime } from 'luxon';
import { updateUniformRequestStatus } from '../../../utils/updateUniformRequest';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import { UnifromRequestForm } from './UniformRequestForm';
import { Employee } from '../../../types/Employee';
import { createUniformRequest } from '../../../utils/createUniformRequest';
import employeeConverter from '../../../converters/employeeConverter';
import { Search, SearchIconWrapper, StyledInputBase } from '../../Search';
import { useDebouncedCallback } from 'use-debounce';
import { ROLES_PERMISSIONS_MAPPING } from '../../../security/Role';
import { SecureComponent } from '../../../security/SecureComponent';

const getBadgeIconFromStatus = (status: UniformRequestStatus | 'All') => {
  if (status === 'All') return undefined;
  else if (status === 'Processing') return <RunCircleRounded color='warning' />;
  else if (status === 'Available') return <MopedRounded color='secondary' />;
  else if (status === 'Completed') return <CheckCircleRounded color='success' />;
  else if (status === 'Cancelled') return <CancelRounded color='error' />;
  return <FiberNewRounded color='info' />;
};

const getBadgeIconFromType = (status: UniformRequestType | 'All') => {
  if (status === 'All') return undefined;
  // else if (status === 'Processing') return <RunCircleRounded color='warning' />;
  // else if (status === 'Available') return <MopedRounded color='secondary' />;
  // else if (status === 'Completed') return <CheckCircleRounded color='success' />;
  // else if (status === 'Cancelled') return <CancelRounded color='error' />;
  return <FiberNewRounded color='info' />;
};

const getIconFromStatus = (status: UniformRequestStatus) => {
  if (status === 'Processing') return <RunCircleRounded />;
  else if (status === 'Available') return <MopedRounded />;
  else if (status === 'Completed') return <CheckCircleRounded />;
  else if (status === 'Cancelled') return <CancelRounded />;
  return <FiberNewRounded />;
};

const getColorFromStatus = (status: UniformRequestStatus) => {
  if (status === 'Processing') return 'warning';
  else if (status === 'Available') return 'secondary';
  else if (status === 'Completed') return 'success';
  else if (status === 'Cancelled') return 'error';
  return 'info';
};

type EmployeeUniformRequest = UniformRequest & {
  occupation: string;
};

export const UniformRequestsPage = () => {
  const db = getFirestore();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [uniformRequests, setUniformRequests] = useState<EmployeeUniformRequest[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<UniformRequestStatus | 'All'>('New');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [filterType, setFilterType] = useState<UniformRequestType | 'All'>('Complete Uniform');
  const [filterTypeAnchorEl, setFilterTypeAnchorEl] = useState<null | HTMLElement>(null);

  // Pagingation
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [firstUniformRequest, setFirstUniformRequest] = useState<QueryDocumentSnapshot | null>(null);
  const [lastUniformRequest, setLastUniformRequest] = useState<QueryDocumentSnapshot | null>(null);

  // Form
  const [employee, setEmployee] = useState<Employee | undefined>();
  const [type, setType] = useState<UniformRequestType>('Complete Uniform');
  const [showUniformRequestForm, setShowUniformRequestForm] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const refresh = (callback?: (q: Query<UniformRequest>) => Query<UniformRequest>) => {
    let q: Query<UniformRequest> = query(collection(db, `UniformRequests`)).withConverter(uniformRequestConverter);

    if (search.trim() === '') 
      q = query(q, orderBy('dateRequested', 'asc'));
    else
      q = query(q, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));
    
    if(callback)
      q = callback(q);
    if (filter !== 'All')
      q = query(q, where('status', '==', filter));
    getDocs(q).then(qSnapshot => {
      // setUniformRequests(qSnapshot.docs.map(d => d.data()));
      Promise.allSettled(qSnapshot.docs.map(async (d) => {
        const data: UniformRequest = d.data();
        // Get employee
        const docSnap = await getDoc(doc(db, `Employees/${data.idNumber}`).withConverter(employeeConverter));
        if (docSnap.exists()) {
          return {
            ...data,
            occupation: docSnap.data().occupation,
          };
        }

        return {
          ...data,
          occupation: '',
        };
      })).then(results => {
        setUniformRequests((results as PromiseFulfilledResult<EmployeeUniformRequest>[]).map((v) => v.value));
        setFirstUniformRequest(qSnapshot.docs[0]);
        setLastUniformRequest(qSnapshot.docs[qSnapshot.docs.length - 1]);
      });
    });

    // Count query
    let qCount: Query = query(collection(db, `UniformRequests`));
    if (search.trim() === '') 
      qCount = query(qCount, orderBy('dateRequested', 'asc'));
    else
      qCount = query(qCount, where('idNumber', '>=', search), where('idNumber', '<=', search + '\uf8ff'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'asc'));
    if (filter !== 'All') 
      qCount = query(qCount, where('status', '==', filter));
    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };

  const searchDebouncedCallback = useDebouncedCallback((value) => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, 1000);

  const handleSearchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
    searchDebouncedCallback(event.currentTarget.value);
  };

  const handleShowUniformRequestForm = () => setShowUniformRequestForm(true);
  const handleCancelUniformRequestForm = () => {
    setShowUniformRequestForm(false);
    setType('Complete Uniform');
  };

  const handleSaveUniformRequest = () => {
    if (employee) {
      if (window.confirm('Are you sure you want to save the uniform request?')) {
        setDisableSaveButton(true);
        createUniformRequest(employee, type).then(r => {
          if (r) {
            setTimeout(() => {
              refresh(q => query(q, limit(rowsPerPage)));
            }, 2500);
            enqueueSnackbar('Uniform request has been created!', { variant: 'success' });
          }
          setEmployee(undefined);
          setType('Complete Uniform');
          setShowUniformRequestForm(false);
          setDisableSaveButton(false);
          setPage(0);
        });
      }
    }
  };

  const handleUniformRequestFormOnChanged = (e: Employee, t: UniformRequestType) => {
    setEmployee(e);
    setType(t);
  };

  const handleFilterButtonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuOnClose = () => setFilterAnchorEl(null);

  const applyFilter = (f: UniformRequestStatus | 'All') => {
    setFilter(f);
    setFilterAnchorEl(null);
  }

  const filterAll = () => applyFilter('All');
  const filterNew = () => applyFilter('New');
  const filterProcessing = () => applyFilter('Processing');
  const filterAvailable = () => applyFilter('Available');
  const filterCompleted = () => applyFilter('Completed');
  

  const handleFilterTypeButtonOnClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterTypeAnchorEl(event.currentTarget);
  };

  const handleFilterTypeMenuOnClose = () => setFilterTypeAnchorEl(null);

  const applyFilterType = (t: UniformRequestType | 'All') => {
    setFilterType(t);
    setFilterTypeAnchorEl(null);
  };

  const filterTypeAll = () => applyFilterType('All');
  const filterTypeCompleteUniform = () => applyFilterType('Complete Uniform');
  const filterTypePantsOnly = () => applyFilterType('Pants Only');
  const filterTypeTopOnly = () => applyFilterType('Top Only');
  const filterTypeCap = () => applyFilterType('Cap');
  const filterTypeShoes = () => applyFilterType('Shoes');

  const handlePageOnChange = (_e: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastUniformRequest), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstUniformRequest), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstUniformRequest), limit(rowsPerPage));
      });
    }
    setPage(to);
  };
  

  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  
  const updateStatus = (uniformRequest: UniformRequest, status: UniformRequestStatus) => {
    if (window.confirm(`Confirm to set status to '${status}'?`)) {
      updateUniformRequestStatus(uniformRequest, status).then(() => {
        setUniformRequests(uniformRequests.map(u => {
          if (u.id === uniformRequest.id) {
            return { ...u, status};
          }

          return u;
        }));
        enqueueSnackbar(`Uniform request has been set to '${status}'!`, { variant: 'success' });
      });
    }
  };

  useEffect(() => {
    refresh(q => query(q, limit(rowsPerPage)));
  }, [filter, rowsPerPage]);

  return (
    <>
      <Container disableGutters>
        <Typography variant="h5" mt={2} mb={5}>Uniform Requests</Typography>
        <Stack direction='row' mb={1}>
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search employee by Id Number"
                inputProps={{ 'aria-label': 'search' }}
                value={search}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <Box flexGrow={1} />
          <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.uniform']}>
            <Button startIcon={<AddRounded />} onClick={handleShowUniformRequestForm}>Create New Request</Button>
          </SecureComponent>
        </Stack>
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={120}>Occupation</TableCell>
                  <TableCell width={220}>Date Requested</TableCell>
                  <TableCell width={150}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Type
                      </Box>
                      <Tooltip title='Filter Type'>
                        <IconButton size='small' onClick={handleFilterTypeButtonOnClick}>
                          <Badge badgeContent={filterType !== 'All' ? getBadgeIconFromType(filterType) : 0}>
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell width={150}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Status
                      </Box>
                      <Tooltip title='Filter Status'>
                        <IconButton size='small' onClick={handleFilterButtonOnClick}>
                          <Badge badgeContent={filter !== 'All' ? getBadgeIconFromStatus(filter) : 0}>
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.uniform']}>
                    <TableCell width={168} align='center'>Actions</TableCell>
                  </SecureComponent>
                  <TableCell width={72} align='center'></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uniformRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant='caption'>
                        No records exist.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {uniformRequests.map((u, i) => {
                  return (
                    <TableRow
                      key={u.id}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      hover
                    >
                      <TableCell align="right">{(i + 1) + (page * rowsPerPage)}</TableCell>
                      <TableCell>
                        <Box>
                          <Box display="flex" flexDirection="row" alignItems="center">
                            <Typography variant='body1'><strong>{u.name}</strong></Typography>
                          </Box>
                          <Typography variant="caption">{u.idNumber}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{u.occupation}</TableCell>
                      <TableCell>{DateTime.fromISO(u.dateRequested).toLocaleString(DateTime.DATETIME_MED)}</TableCell>
                      <TableCell>{u.type}</TableCell>
                      <TableCell align='center'>
                        <Chip variant='outlined' color={getColorFromStatus(u.status)} icon={getIconFromStatus(u.status)} label={u.status} />
                      </TableCell>
                      <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.uniform']}>
                        <TableCell>
                          <Stack direction='row' spacing={1}>
                            <Tooltip title='Set as Processing'>
                              <span>
                                <IconButton color='warning' disabled={u.status !== 'New'} onClick={() => updateStatus(u, 'Processing')}><RunCircleRounded /></IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Set as Ready for Pickup'>
                              <span>
                                <IconButton color='secondary' disabled={u.status !== 'New' && u.status !== 'Processing'} onClick={() => updateStatus(u, 'Available')}><MopedRounded /></IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title='Set as Completed'>
                              <span>
                                <IconButton color='success' disabled={u.status === 'Completed' || u.status === 'Cancelled'} onClick={() => updateStatus(u, 'Completed')}><CheckRounded /></IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </SecureComponent>
                      <TableCell>
                        <Tooltip title='View'>
                          <IconButton color='info' onClick={() => navigate(`/dashboard/uniformRequests/${u.id}`)}><ArrowForwardIosRounded /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
      </Container>
      <SecureComponent permissions={ROLES_PERMISSIONS_MAPPING['editor.uniform']}>
        <Dialog open={showUniformRequestForm} onClose={handleCancelUniformRequestForm} fullWidth>
          <DialogTitle>Create Uniform Request</DialogTitle>
          <DialogContent dividers>
            <UnifromRequestForm employee={employee} onChanged={handleUniformRequestFormOnChanged} />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={handleCancelUniformRequestForm}>CANCEL</Button>
            <Button color="primary" onClick={handleSaveUniformRequest} disabled={disableSaveButton}>SAVE</Button>
          </DialogActions>
        </Dialog>
      </SecureComponent>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={filterAll}>
          <ListItemIcon></ListItemIcon>
          All
        </MenuItem>
        <MenuItem onClick={filterNew}>
          <ListItemIcon>
            <FiberNewRounded color='info' />
          </ListItemIcon>
          New
        </MenuItem>
        <MenuItem onClick={filterProcessing}>
          <ListItemIcon>
            <RunCircleRounded color='warning' />
          </ListItemIcon>
          Processing
        </MenuItem>
        <MenuItem onClick={filterAvailable}>
          <ListItemIcon>
            <MopedRounded color='secondary' />
          </ListItemIcon>
          Available
        </MenuItem>
        <MenuItem onClick={filterCompleted}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Completed
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={filterTypeAnchorEl}
        open={Boolean(filterTypeAnchorEl)}
        onClose={handleFilterTypeMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={filterTypeAll}>
          <ListItemIcon></ListItemIcon>
          All
        </MenuItem>
        <MenuItem onClick={filterTypeCompleteUniform}>
          <ListItemIcon>
            <FiberNewRounded color='info' />
          </ListItemIcon>
          Complete Uniform
        </MenuItem>
        <MenuItem onClick={filterTypePantsOnly}>
          <ListItemIcon>
            <RunCircleRounded color='warning' />
          </ListItemIcon>
          Pants Only
        </MenuItem>
        <MenuItem onClick={filterTypeTopOnly}>
          <ListItemIcon>
            <MopedRounded color='secondary' />
          </ListItemIcon>
          Top Only
        </MenuItem>
        <MenuItem onClick={filterTypeCap}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Cap
        </MenuItem>
        <MenuItem onClick={filterTypeShoes}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Shoes
        </MenuItem>
      </Menu>
    </>
  )
}